import { useCallback, useState, useEffect } from 'react';
import { rollD20, rollD6 } from '../utils/dice.js';
import { getEvent } from '../utils/events.js';
import { getStoryEvent } from '../config/storyEvents.js';
import { applyEventEffect, checkBirth } from '../systems/eventSystem.js';
import { rollAllTasks, pushTask } from '../systems/taskSystem.js';
import { injureWorker, addRefugees, createWorker, createImmatureWorker } from '../systems/workerSystem.js';
import { startBuilding } from '../systems/buildingSystem.js';
import { checkVictory, checkDefeat } from '../systems/victorySystem.js';
import { useEndDay } from './useEndDay.js';
import { useAudio } from './useAudio.js';
import { executeTrade } from '../systems/tradeSystem.js';
import { hasSpecialMechanic } from '../systems/taskEffectsSystem.js';

/**
 * Manages phase transitions and provides handlers for each phase
 */
export function usePhaseManager(gameState, announce) {
  const { game, setGame, assignWorker, setBountyTask, reset } = gameState;
  const [showBuildSelect, setShowBuildSelect] = useState(false);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [tradeDialogData, setTradeDialogData] = useState(null);
  const audio = useAudio();

  const handleEndDay = useEndDay({ game, setGame });

  // Initialize event on mount and day start
  useEffect(() => {
    if (game.phase === 'event' && !game.currentEvent) {
      // Check for story event first
      const storyEvent = getStoryEvent(game.day, game);

      let event, eventRoll;
      if (storyEvent) {
        event = storyEvent;
        eventRoll = null; // Story events don't have d20 rolls
      } else {
        eventRoll = rollD20();
        event = getEvent(eventRoll);
        // Play d20 roll sound
        audio.playDice('d20', 1);
      }

      // NEW: Check for secondary events (births, disease, refugees)
      const secondaryEvents = [];

      // Check for birth
      const birthEvent = checkBirth(game);
      if (birthEvent) {
        secondaryEvents.push(birthEvent);
      }

      // Check for scheduled refugee arrival notice
      if (game.futureRefugeesArrivalNotice) {
        secondaryEvents.push(game.futureRefugeesArrivalNotice);
      }

      // Check for disease notice from previous day
      if (game.diseaseNotice) {
        secondaryEvents.push(game.diseaseNotice);
      }

      setGame(g => ({
        ...g,
        eventRoll,
        currentEvent: event,
        secondaryEvents, // NEW field
        tempWorker: event.type === 'traveler' ? { id: 'temp', status: 'healthy' } : null,
        futureRefugeesArrivalNotice: null, // Clear after displaying
        diseaseNotice: null // Clear after displaying
      }));

      const announcements = [
        `Day ${game.day}. Event: ${event.name}. ${event.description}`
      ];

      if (secondaryEvents.length > 0) {
        secondaryEvents.forEach(se => announcements.push(se.message));
      }

      announce(announcements.join(' '));
    }
  }, [game, setGame, announce]);

  // Initialize assignments
  useEffect(() => {
    if (game.phase === 'assign' && Object.keys(game.assignments).length === 0) {
      const aliveWorkers = game.workers.filter(w => w.status !== 'dead');

      // BUGFIX: Defensive check - if no workers alive, transition to game over
      // This catches edge cases where workers died between Event and Assignment phases
      if (aliveWorkers.length === 0) {
        setGame(g => ({
          ...g,
          phase: 'gameover',
          gameOver: true,
          gameWon: false,
          ending: {
            type: 'defeat',
            icon: '💀',
            title: 'The Settlement Falls',
            message: `Your settlement fell on day ${g.day}. The wilderness claims another dream...`,
            color: 'text-red-400'
          }
        }));
        announce('Game over. Your settlement has fallen.');
        return;
      }

      const allWorkers = game.tempWorker ? [...aliveWorkers, game.tempWorker] : aliveWorkers;

      const defaultAssignments = {};
      allWorkers.forEach(w => {
        // Use last assignment if available, otherwise use smart default
        const lastAssignment = game.lastAssignments?.[w.id];
        const smartDefault = w.status === 'injured' ? 'rest' : 'farm';
        defaultAssignments[w.id] = lastAssignment || smartDefault;
      });

      setGame(g => ({ ...g, assignments: defaultAssignments }));
    }
  }, [game.phase, game.assignments, game.workers, game.tempWorker, game.lastAssignments, setGame, announce]);

  // Event phase handlers
  const handleContinueFromEvent = useCallback(() => {
    const event = game.currentEvent;
    const stateWithEffects = applyEventEffect(event, game);

    // Check if all workers died after applying event effects and secondary events
    let finalWorkers = stateWithEffects.workers;
    if (game.secondaryEvents && game.secondaryEvents.length > 0) {
      game.secondaryEvents.forEach(se => {
        if (se.type === 'birth') {
          finalWorkers = [...finalWorkers, se.worker];
        }
      });
    }
    const aliveWorkers = finalWorkers.filter(w => w.status !== 'dead');
    const isGameOver = aliveWorkers.length === 0;

    setGame(g => {
      let updates = { ...stateWithEffects };

      // NEW: Apply secondary event effects (births)
      if (g.secondaryEvents && g.secondaryEvents.length > 0) {
        g.secondaryEvents.forEach(se => {
          if (se.type === 'birth') {
            updates.workers = [...updates.workers, se.worker];
            updates.morale = Math.min(100, (updates.morale || 50) + se.moraleBonus);
          }
        });
        updates.secondaryEvents = []; // Clear after applying
      }

      // BUGFIX: Check if all workers died before transitioning to Assignment phase
      // This prevents rendering Assignment UI with 0 workers
      const aliveWorkers = updates.workers.filter(w => w.status !== 'dead');
      if (aliveWorkers.length === 0) {
        updates.phase = 'gameover';
        updates.gameOver = true;
        updates.gameWon = false;
        updates.ending = {
          type: 'defeat',
          icon: '💀',
          title: 'The Settlement Falls',
          message: `Your settlement fell on day ${updates.day}. The wilderness claims another dream...`,
          color: 'text-red-400'
        };
        return updates;
      }

      updates.phase = 'assign';
      return updates;
    });

    // Update announcements based on game state
    if (isGameOver) {
      announce('Game over. Your settlement has fallen.');
    } else if (event.type === 'raid') {
      announce('Raiders are attacking! Assign workers to guard.');
      // Sound already played in EventCard
    } else if (event.type === 'warning') {
      announce('Raid warning! Attack coming tomorrow.');
      // Sound already played in EventCard
    } else {
      announce('Assign your workers to tasks.');
    }
  }, [game, setGame, announce, audio]);

  const handleStoryChoice = useCallback((choiceId) => {
    const choice = game.currentEvent.choices.find(c => c.id === choiceId);

    if (!choice) return;

    // Check if choice has resource costs
    if (choice.cost) {
      const canAfford =
        (!choice.cost.food || game.food >= choice.cost.food) &&
        (!choice.cost.wood || game.wood >= choice.cost.wood) &&
        (!choice.cost.stone || game.stone >= choice.cost.stone) &&
        (!choice.cost.metal || (game.metal || 0) >= choice.cost.metal);

      if (!canAfford) {
        announce('Not enough resources for this choice!');
        audio.playSound('ui_error');
        return;
      }
    }

    setGame(g => {
      let updates = { ...g };

      // Deduct costs
      if (choice.cost) {
        if (choice.cost.food) updates.food = g.food - choice.cost.food;
        if (choice.cost.wood) updates.wood = g.wood - choice.cost.wood;
        if (choice.cost.stone) updates.stone = g.stone - choice.cost.stone;
        if (choice.cost.metal) updates.metal = (g.metal || 0) - choice.cost.metal;
      }

      // Apply choice effects
      if (choice.effects) {
        // Direct resource/morale updates
        if (choice.effects.knowledge !== undefined) {
          updates.knowledge = (g.knowledge || 0) + choice.effects.knowledge;
        }
        if (choice.effects.metal !== undefined) {
          updates.metal = (g.metal || 0) + choice.effects.metal;
        }
        if (choice.effects.morale !== undefined) {
          if (typeof choice.effects.morale === 'number' && choice.effects.morale > 50) {
            updates.morale = choice.effects.morale; // Set morale
          } else {
            updates.morale = (g.morale || 50) + choice.effects.morale; // Add to morale
          }
        }

        // Future refugees
        if (choice.effects.futureRefugeesDay) {
          updates.futureRefugees = {
            day: choice.effects.futureRefugeesDay,
            count: choice.effects.futureRefugeesCount
          };
        }

        // Add workers immediately
        if (choice.effects.workers) {
          const newWorkers = addRefugees(g.workers, choice.effects.workers, g.day);
          updates.workers = newWorkers;
        }

        // Metal roll (1d6)
        if (choice.effects.metalRoll) {
          updates.metal = (g.metal || 0) + rollD6();
        }

        // Building progress (e.g., wallProgress)
        if (choice.effects.wallProgress) {
          const wall = g.buildings.find(b => b.type === 'wall');
          if (wall && !wall.complete) {
            updates.buildings = g.buildings.map(b =>
              b.type === 'wall' ? { ...b, progress: b.progress + choice.effects.wallProgress } : b
            );
          }
        }

        // Temporary bonuses
        if (choice.effects.restBonusDays) {
          updates.restBonus = {
            endsOn: g.day + choice.effects.restBonusDays,
            amount: 1
          };
        }
        if (choice.effects.farmBonusDays) {
          updates.farmBonus = {
            endsOn: g.day + choice.effects.farmBonusDays,
            amount: choice.effects.farmBonusAmount || 5
          };
        }
      }

      // NEW: Recruit traveler permanently
      if (choice.effects.recruitTraveler) {
        const maxId = Math.max(...g.workers.map(w => typeof w.id === 'number' ? w.id : 0));
        const newWorker = createWorker(maxId + 1, g.day);
        updates.workers = [...g.workers, newWorker];
        updates.tempWorker = null; // Don't create temp worker
      }

      // NEW: Handle temp worker for traveler event
      if (choice.effects.tempWorker === true) {
        updates.tempWorker = { id: 'temp', status: 'healthy' };
      } else if (choice.effects.tempWorker === false || choice.effects.recruitTraveler) {
        updates.tempWorker = null;
      }

      // Handle immature workers from baby boom event
      if (choice.effects.immatureWorkers) {
        const count = choice.effects.immatureWorkers;
        const hasNursery = g.buildings.some(b => b.type === 'nursery' && b.complete);
        const maturityDays = hasNursery ? 3 : 5;

        const newWorkers = [...updates.workers];
        for (let i = 0; i < count; i++) {
          const maxId = Math.max(...newWorkers.map(w => typeof w.id === 'number' ? w.id : 0));
          const baby = createImmatureWorker(maxId + 1, g.day, maturityDays);
          newWorkers.push(baby);
        }
        updates.workers = newWorkers;
      }

      // Handle building cost reduction bonus
      if (choice.effects.buildingBonus) {
        updates.buildingCostReduction = 0.2; // 20% discount on next building
      }

      // Save choice
      updates.choicesMade = { ...g.choicesMade, [g.day]: choiceId };
      updates.phase = 'assign';

      return updates;
    });

    announce(choice.effects.message || 'Choice made.');
  }, [game, setGame, announce]);

  // Assignment phase handlers
  const handleAssign = useCallback((workerId, task) => {
    // Check if task requires selection (e.g., build without a project)
    if (hasSpecialMechanic(task, 'requires_selection') && !game.buildingInProgress) {
      assignWorker(workerId, task);
      setShowBuildSelect(true);
      return;
    }

    assignWorker(workerId, task);
  }, [game.buildingInProgress, assignWorker]);

  const handleSelectBuilding = useCallback((type) => {
    const result = startBuilding(type, {
      food: game.food,
      wood: game.wood,
      stone: game.stone,
      metal: game.metal || 0
    }, game.buildings);

    if (result) {
      setGame(g => ({
        ...g,
        food: result.resources.food,
        wood: result.resources.wood,
        stone: result.resources.stone,
        metal: result.resources.metal || 0,
        buildingInProgress: result.buildingInProgress,
        buildings: [...g.buildings, result.building]
      }));

      setShowBuildSelect(false);
      announce(`Started building.`);
      audio.playSound('task_build');
    }
  }, [game, setGame, announce, audio]);

  const handleRoll = useCallback(() => {
    const rolls = rollAllTasks(game.workers, game.assignments, game.tempWorker, game.morale || 50);

    // Check if trade task was assigned
    const hasTrade = rolls.trade && rolls.trade.rolls.length > 0;

    if (hasTrade) {
      // Open trade dialog instead of going to results
      const tradeTotal = rolls.trade.rolls.reduce((a, b) => a + b, 0);
      setTradeDialogData({
        diceTotal: tradeTotal,
        rolls: rolls
      });
      setShowTradeDialog(true);
      setGame(g => ({ ...g, rolls })); // Save rolls for later
    } else {
      setGame(g => ({ ...g, phase: 'results', rolls }));
      announce('Dice rolled! Review results and decide whether to push.');
    }
  }, [game.workers, game.assignments, game.tempWorker, game.morale, setGame, announce]);

  // Results phase handlers
  const handlePush = useCallback((task) => {
    const result = pushTask(task, game.buildings);

    // Play push feedback sound
    if (result.success) {
      audio.playSound('ui_positive');
    } else {
      audio.playSound('ui_negative');
    }

    setGame(g => {
      const newPushes = {
        ...g.pushes,
        [task]: result
      };

      // If push failed, injure a worker on that task
      let newWorkers = g.workers;
      if (!result.success) {
        const workerIds = g.rolls[task].workers;
        const targetId = workerIds.find(id => id !== 'temp');
        if (targetId) {
          newWorkers = injureWorker(g.workers, targetId, true);
        }
      }

      return { ...g, pushes: newPushes, workers: newWorkers };
    });

    announce(result.success
      ? `Push successful! Added ${result.value} to ${task}.`
      : `Push failed with ${result.value}! Worker injured.`
    );
  }, [game.buildings, setGame, announce, audio]);

  const handleSelectBounty = useCallback((task) => {
    setBountyTask(task);
    announce(`Bounty applied to ${task}! Output doubled.`);
  }, [setBountyTask, announce]);

  // Combat phase handlers
  const handleContinueFromCombat = useCallback(() => {
    const remainingWorkers = game.workers.filter(w => w.status !== 'dead');
    const gameOver = checkDefeat(remainingWorkers);
    const gameWon = checkVictory(game.day, remainingWorkers);

    setGame(g => ({
      ...g,
      phase: (gameOver || gameWon) ? 'gameover' : 'event',
      day: (gameOver || gameWon) ? g.day : g.day + 1,
      combatResult: null,
      currentEvent: null,
      gameOver,
      gameWon
    }));
  }, [game, setGame]);

  // Trade dialog handlers
  const handleConfirmTrade = useCallback((tradeResult) => {
    setGame(g => {
      const updatedResources = executeTrade(tradeResult, {
        food: g.food,
        wood: g.wood,
        stone: g.stone,
        metal: g.metal || 0
      });

      return {
        ...g,
        food: updatedResources.food,
        wood: updatedResources.wood,
        stone: updatedResources.stone,
        metal: updatedResources.metal,
        phase: 'results' // Continue to results after trade
      };
    });

    setShowTradeDialog(false);
    setTradeDialogData(null);
    announce(`Traded ${tradeResult.cost} ${tradeResult.fromResource} for ${tradeResult.gained} ${tradeResult.toResource}`);
  }, [setGame, announce]);

  const handleCancelTrade = useCallback(() => {
    setShowTradeDialog(false);
    setTradeDialogData(null);
    setGame(g => ({ ...g, phase: 'results' }));
  }, [setGame]);

  return {
    showBuildSelect,
    setShowBuildSelect,
    showTradeDialog,
    tradeDialogData,
    eventHandlers: {
      event: game.currentEvent,
      eventRoll: game.eventRoll,
      secondaryEvents: game.secondaryEvents, // NEW
      onContinue: handleContinueFromEvent,
      onStoryChoice: handleStoryChoice
    },
    assignHandlers: {
      workers: game.workers,
      assignments: game.assignments,
      tempWorker: game.tempWorker,
      buildings: game.buildings,
      buildingInProgress: game.buildingInProgress,
      onAssign: handleAssign,
      onRoll: handleRoll
    },
    resultsHandlers: {
      rolls: game.rolls,
      pushes: game.pushes,
      event: game.currentEvent,
      buildings: game.buildings,
      bountyTask: game.bountyTask,
      onPush: handlePush,
      onSelectBounty: handleSelectBounty,
      onEndDay: handleEndDay
    },
    combatHandlers: {
      result: game.combatResult,
      onContinue: handleContinueFromCombat
    },
    buildHandlers: {
      buildings: game.buildings,
      wood: game.wood,
      stone: game.stone,
      metal: game.metal,
      day: game.day,
      techPoints: game.techPoints,
      knowledge: game.knowledge,
      morale: game.morale,
      workers: game.workers,
      raidsDefeated: game.raidsDefeated,
      onSelect: handleSelectBuilding,
      onCancel: () => setShowBuildSelect(false)
    },
    tradeHandlers: {
      data: tradeDialogData,
      currentResources: {
        food: game.food,
        wood: game.wood,
        stone: game.stone,
        metal: game.metal || 0
      },
      onConfirmTrade: handleConfirmTrade,
      onCancel: handleCancelTrade
    },
    gameOverHandlers: {
      won: game.gameWon,
      day: game.day,
      ending: game.ending,
      onRestart: reset
    }
  };
}
